import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EFormState } from '@feathr/blackbox';
import { Button, Modal, Text, toast } from '@feathr/components';

import { DomainAvailabilityStates, EmbedFlow, EmbedTypeSelector, useEmbedForm } from './EmbedModal';
import { useDonationFormBuilder } from './useDonationFormBuilder';

import * as styles from './DonationFormEmbedModal.css';

export type EmbedType = 'inline' | 'popup' | undefined;

function DonationFormEmbedModal(): JSX.Element {
  const { t } = useTranslation();
  const {
    donationForm,
    isEmbedModalOpen,
    closeEmbedModal,
    availableContentDomains,
    isLoadingDomains,
  } = useDonationFormBuilder();
  const [embedType, setEmbedType] = useState<EmbedType>();

  /**
   * Derive domain availability state from context.
   */
  const hasAvailableDomains = availableContentDomains ? availableContentDomains.length > 0 : false;
  const domainCount = availableContentDomains?.length ?? 0;

  const {
    redirectDomainId,
    contentDomain,
    isCodeReady,
    hasAllSnippetsCopied,
    handleSelectDomain,
    handleGenerateCode,
    handleCodeCopied,
    resetState,
  } = useEmbedForm();

  /**
   * Auto-select domain if there's only one available.
   * Only runs after embed type is selected.
   */
  useEffect(() => {
    if (
      isEmbedModalOpen &&
      embedType &&
      availableContentDomains?.length === 1 &&
      !redirectDomainId
    ) {
      const singleDomain = availableContentDomains[0];
      handleSelectDomain({
        id: singleDomain.id,
        name: singleDomain.get('content_domain'),
      });
    }
  }, [availableContentDomains, embedType, handleSelectDomain, isEmbedModalOpen, redirectDomainId]);

  // Reset modal state when it closes
  useEffect(() => {
    if (isEmbedModalOpen) {
      return;
    }

    setEmbedType(undefined);
    resetState();
  }, [isEmbedModalOpen, resetState]);

  async function onGenerateCode(): Promise<void> {
    // Form state must be set to published in order for stats to work.
    if (donationForm.get('state') !== EFormState.Published) {
      try {
        donationForm.set({ state: EFormState.Published });
        await donationForm.patchDirty();
      } catch (error) {
        toast(t('Failed to publish form'), {
          type: 'error',
        });
        return;
      }
    }

    await handleGenerateCode();
  }

  const handleClose = (): void => {
    closeEmbedModal();
  };

  const description = useMemo(() => {
    if (!embedType) {
      return t(
        "Choose how you'd like your donation form to appear on your website. You can either display it directly on the page (inline) or open it in a pop-up modal when someone clicks a button.",
      );
    }
    return '';
  }, [embedType, t]);

  const getModalRightActions = (): JSX.Element[] => {
    if (!embedType) {
      return [
        <Button key={'cancel'} onClick={handleClose}>
          {t('Cancel')}
        </Button>,
      ];
    }

    // Both inline and popup have same buttons: Cancel | Done
    return [
      <Button key={'cancel'} onClick={handleClose}>
        {t('Cancel')}
      </Button>,
      <Button
        disabled={!hasAllSnippetsCopied(embedType)}
        key={'done'}
        onClick={handleClose}
        type={'primary'}
      >
        {t('Done')}
      </Button>,
    ];
  };

  return (
    <Modal
      onClose={handleClose}
      opened={isEmbedModalOpen}
      rightActions={getModalRightActions()}
      title={t('Embed your donation form')}
    >
      {description && (
        <Text.DefaultRegular className={styles.modalDescription}>{description}</Text.DefaultRegular>
      )}

      {/* Step 1: Show embed type selector first */}
      {!embedType && (
        <EmbedTypeSelector
          onSelectInline={(): void => setEmbedType('inline')}
          onSelectPopup={(): void => setEmbedType('popup')}
          selectedType={embedType}
        />
      )}

      {/* Step 2: After embed type is selected, check domains */}
      {embedType && (
        <>
          <DomainAvailabilityStates
            hasAvailableDomains={hasAvailableDomains}
            isLoading={isLoadingDomains}
          />

          {/* Domains available - show embed flow */}
          {hasAvailableDomains && (
            <EmbedFlow
              contentDomain={contentDomain}
              donationForm={donationForm}
              embedType={embedType}
              isCodeReady={isCodeReady}
              onCodeCopied={handleCodeCopied}
              onGenerateCode={onGenerateCode}
              onSelectDomain={handleSelectDomain}
              redirectDomainId={redirectDomainId}
              showDomainSelect={domainCount > 1}
            />
          )}
        </>
      )}
    </Modal>
  );
}

export default observer(DonationFormEmbedModal);
